import { FormBuilder, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { NgLabelTemplateDirective, NgOptionTemplateDirective, NgSelectComponent } from "@ng-select/ng-select";
import { Technician } from "src/app/_models";
import { ProjectWithPrioStatus } from "src/app/_models/project.interface";
import { TechnicianCompany } from "src/app/_models/technician.interface";
import { TechnicianService } from "src/app/_services/technician.service";

import { ActivatedRoute, RouterModule } from "@angular/router";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { NgSelectModule } from "@ng-select/ng-select";

import { Component, OnInit, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { addDays, format as formatDate, startOfWeek } from "date-fns";
import { ProjectService } from "src/app/_services";
import { DateInputModule } from "../_shared/date-input";

@Component({
  selector: "hawk-ticket-search-form",
  templateUrl: "./ticket-search-form.component.html",
  styleUrls: ["./ticket-search-form.component.scss"],
  imports: [
    ReactiveFormsModule,
    FormsModule,
    NgSelectComponent,
    NgLabelTemplateDirective,
    NgOptionTemplateDirective,
    RouterModule,
    NgSelectModule,
    FontAwesomeModule,
    DateInputModule,
  ],
  host: {
    class: "app-page",
  },
})
export class TicketSearchFormComponent implements OnInit {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private projectService = inject(ProjectService);
  private technicianService = inject(TechnicianService);

  public projects: ProjectWithPrioStatus[] = [];
  public technicians: Technician[] = [];
  public companies: TechnicianCompany[] = [];
  public selectedTechnicians: number[] = [];
  public searchTerm: string = "";
  public searchTermProject: string = "";
  public searchTermStatus: string = "";
  public searchTermPrio: string = "";
  public currentProject: ProjectWithPrioStatus = null;

  public form = this.fb.group({
    order_number: this.fb.control(""),
    external_order_number: this.fb.control(""),
    fault_description: this.fb.control(""),
    invoice_number: this.fb.control(""),
    invoice_number_partner: this.fb.control(""),
    project_id: this.fb.control<number>(null),
    status_id: this.fb.control<number>(null),
    priority_id: this.fb.control<number>(null),
    technician_ids: this.fb.control<number[]>([]),
    technician_company_names: this.fb.control<string[]>([]),
    is_open: this.fb.control(""),
    not_invoiced: this.fb.control(""),
    not_invoiced_partner: this.fb.control(""),
    has_date: this.fb.control(""),
    create_date_from: this.fb.control(null),
    create_date_to: this.fb.control(null),
    operation_date_from: this.fb.control(null),
    operation_date_to: this.fb.control(null),
  });

  constructor() {
    this.useQueryParams();
    this.form.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      this.updateQueryParams();
    });
  }

  ngOnInit(): void {
    this.technicianService.getAll().subscribe({
      next: (data) => {
        this.technicians = data;
        const uniqueFilter = (value, index, self) => self.indexOf(value) === index;
        this.companies = this.technicians
          .map((technician) => technician.company)
          .filter((companyName) => !!companyName)
          .filter(uniqueFilter)
          .sort()
          .map((companyName) => ({ company: companyName }) as TechnicianCompany);
      },
      error: (err) => {
        console.error(err);
      },
    });

    this.projectService.getActiveWithPrioStatus().subscribe({
      next: (data) => {
        this.projects = data;
      },
      error: (err) => {
        console.error(err);
      },
    });

    this.onFormChanges();
  }

  public search() {
    const searchParams = this.getFormParams();
    this.router.navigate(["ticket", "suche"], { queryParams: searchParams });
    return false;
  }

  protected getFormParams() {
    const formdata = this.form.value;
    return {
      order_number: formdata.order_number,
      external_order_number: formdata.external_order_number,
      fault_description: formdata.fault_description,
      invoice_number: formdata.invoice_number,
      invoice_number_partner: formdata.invoice_number_partner,
      project_id: formdata.project_id,
      priority_id: formdata.priority_id,
      status_id: formdata.status_id,

      create_date_from: formdata.create_date_from ? formatDate(formdata.create_date_from, "yyyy-MM-dd") : null,
      create_date_to: formdata.create_date_to ? formatDate(formdata.create_date_to, "yyyy-MM-dd") : null,
      operation_date_from: formdata.operation_date_from ? formatDate(formdata.operation_date_from, "yyyy-MM-dd") : null,
      operation_date_to: formdata.operation_date_to ? formatDate(formdata.operation_date_to, "yyyy-MM-dd") : null,

      technician_ids: formdata.technician_ids,
      technician_company_names: formdata.technician_company_names,
      is_open: formdata.is_open,
      not_invoiced: formdata.not_invoiced,
      not_invoiced_partner: formdata.not_invoiced_partner,
      has_date: formdata.has_date,
    };
  }

  protected useQueryParams() {
    const params = this.route.snapshot.queryParams;
    for (const key in this.form.controls) {
      const control = this.form.get(key);
      const param = params[key];
      if (!control || !param) {
        continue;
      }
      control.setValue(param);
    }
  }

  protected updateQueryParams() {
    const params = this.getFormParams();
    for (const key in params) {
      if (!params[key]) {
        delete params[key];
      }
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: params,
      queryParamsHandling: "merge",
      replaceUrl: true,
    });
  }

  public onFormChanges(): void {
    this.form.get("project_id").valueChanges.subscribe((val) => {
      this.currentProject = this.projects.filter((project) => project.id == val)[0];

      this.form.patchValue({ status_id: null, priority_id: null });
    });
  }

  public setRange(target: string, range: string) {
    let start: any;
    let end: any;
    switch (range) {
      case "today":
        start = addDays(new Date(), 0);
        end = addDays(new Date(), 0);
        break;
      case "thisweek":
        start = startOfWeek(new Date(), { weekStartsOn: 1 });
        end = addDays(start, 6);
        break;
      case "nextweek":
        start = addDays(startOfWeek(new Date(), { weekStartsOn: 1 }), 7);
        end = addDays(start, 6);
        break;
      default:
        return;
    }
    if (target == "created") {
      this.form.controls.create_date_from.setValue(start);
      this.form.controls.create_date_to.setValue(end);
    }
    if (target == "operation") {
      this.form.controls.operation_date_from.setValue(start);
      this.form.controls.operation_date_to.setValue(end);
    }
  }

  public clearCreateDate() {
    this.form.controls.create_date_from.setValue(null);
    this.form.controls.create_date_to.setValue(null);
  }

  public clearOperationDate() {
    this.form.controls.operation_date_from.setValue(null);
    this.form.controls.operation_date_to.setValue(null);
  }
}
